/*
 * AI Variant Badges for Shopify Minimog
 * Displays Most Popular (upper middle option) and Best Value (last option)
 * according to settings in the owning Shopify product section.
 */
(() => {
  'use strict';

  if (window.__aiVariantBadgesInitialized) return;
  window.__aiVariantBadgesInitialized = true;

  const CONFIG_SELECTOR = '.ai-vb-config';
  const GROUP_SELECTOR = 'variant-picker [data-picker-field="radio"][data-option-name]';
  const RELEVANT_SELECTOR = [
    CONFIG_SELECTOR,
    'variant-picker',
    '[data-picker-field="radio"][data-option-name]',
    '.m-product-option--content',
    '.m-product-option--node',
    'label.m-product-option--node__label'
  ].join(',');

  const normalize = (value) => String(value || '').trim().toLocaleLowerCase();

  function setCSSVariable(section, name, value) {
    if (value && section.style.getPropertyValue(name) !== value) {
      section.style.setProperty(name, value);
    }
  }

  function updateBadge(node, label, type, text) {
    const badges = Array.from(label.children).filter((child) =>
      child.classList.contains('ai-vb-badge')
    );

    // In case another renderer duplicated this badge, keep only the first one.
    badges.slice(1).forEach((badge) => badge.remove());
    let badge = badges[0];
    const shouldShow = Boolean(type && text);

    if (!shouldShow) {
      if (badge) badge.remove();
      node.classList.remove('ai-vb-option-node');
      label.classList.remove('ai-vb-option-label');
      return false;
    }

    if (!badge) {
      badge = document.createElement('span');
      label.appendChild(badge);
    }

    const className = `ai-vb-badge ai-vb-badge--${type}`;
    if (badge.className !== className) badge.className = className;
    if (badge.textContent !== text) badge.textContent = text;

    node.classList.add('ai-vb-option-node');
    label.classList.add('ai-vb-option-label');
    return true;
  }

  function renderGroup(group, config, enabledNames) {
    const content = group.querySelector('.m-product-option--content');
    if (!content) return;

    // Only direct options, not nested product components.
    const nodes = Array.from(content.children).filter((el) =>
      el.matches('.m-product-option--node')
    );

    const enabled = config.dataset.enabled === 'true' &&
      enabledNames.has(normalize(group.dataset.optionName));

    const count = nodes.length;
    const showPopular = enabled && config.dataset.showPopular === 'true' && count >= 3;
    const showBest = enabled && config.dataset.showBest === 'true' && count >= 2;
    const popularText = (config.dataset.popularText || '').trim();
    const bestText = (config.dataset.bestText || '').trim();

    const popularIndex = Math.floor(count / 2);
    const bestIndex = count - 1;
    let hasBadges = false;

    nodes.forEach((node, index) => {
      const label = node.querySelector('label.m-product-option--node__label');
      if (!label) return;

      let type = null;
      let text = '';

      if (showBest && index === bestIndex) {
        type = 'best';
        text = bestText;
      } else if (showPopular && index === popularIndex) {
        type = 'popular';
        text = popularText;
      }

      if (updateBadge(node, label, type, text)) hasBadges = true;
    });

    content.classList.toggle('ai-vb-content', hasBadges);
  }

  function refresh() {
    document.querySelectorAll(CONFIG_SELECTOR).forEach((config) => {
      const section = config.closest('.shopify-section');
      if (!section) return;

      const enabledNames = new Set(
        (config.dataset.optionNames || '')
          .split(/[,;\n]+/)
          .map(normalize)
          .filter(Boolean)
      );

      setCSSVariable(section, '--ai-vb-popular-bg', config.dataset.popularBg);
      setCSSVariable(section, '--ai-vb-popular-color', config.dataset.popularColor);
      setCSSVariable(section, '--ai-vb-best-bg', config.dataset.bestBg);
      setCSSVariable(section, '--ai-vb-best-color', config.dataset.bestColor);

      section.querySelectorAll(GROUP_SELECTOR).forEach((group) => {
        renderGroup(group, config, enabledNames);
      });
    });
  }

  let pending = false;
  function scheduleRefresh() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      refresh();
    });
  }

  function relevantInsertion(node) {
    return node.nodeType === Node.ELEMENT_NODE &&
      (node.matches(RELEVANT_SELECTOR) || Boolean(node.querySelector(RELEVANT_SELECTOR)));
  }

  function init() {
    refresh();

    // Catch picker re-renders, AJAX-loaded products and Shopify editor sections.
    // Badge insertions do not match RELEVANT_SELECTOR and cannot cause a loop.
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (Array.from(mutation.addedNodes).some(relevantInsertion)) {
          scheduleRefresh();
          return;
        }
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    document.addEventListener('shopify:section:load', scheduleRefresh);
    document.addEventListener('shopify:section:select', scheduleRefresh);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
